import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { logoutUser } from "./appSlice";
import { clearAfterPurchase } from "./checkoutSlice";

const baseUrl = process.env.REACT_APP_API_URL;

// ============================================================
// Helper: construir query string a partir de un objeto de filtros
// ============================================================
function buildQueryString(params) {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );
  if (entries.length === 0) return "";
  return "?" + entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
}

// ============================================================
// Helper: hacer fetch autenticado con manejo de errores
// - AbortController con timeout de 10 segundos
// - Redirección a /log-in en 401 (sesión expirada)
// - Redirección a / en 403 (rol insuficiente)
// ============================================================
async function apiFetch(endpoint, token, { dispatch } = {}) {
  const url = `${baseUrl}/${endpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.status === 401) {
      if (dispatch) {
        dispatch(logoutUser());
        dispatch(clearAfterPurchase());
      }
      window.location.href = "/log-in";
      throw new Error("Sesión expirada");
    }

    if (response.status === 403) {
      window.location.href = "/";
      throw new Error("No autorizado");
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Error desconocido");
      throw new Error(errorText || `HTTP ${response.status}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error("Tiempo de espera agotado. Intente nuevamente.");
    }
    throw error;
  }
}

// ============================================================
// Async Thunks
// ============================================================

// --- Inventario ---
export const fetchInventario = createAsyncThunk(
  "bi/fetchInventario",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().app.user;
      const { filters, pagination } = getState().bi.inventario;
      const params = { ...filters, ...pagination };
      const qs = buildQueryString(params);
      return await apiFetch(`bi/inventario${qs}`, token, { dispatch });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- Inventario Simple (resumen: una fila por producto) ---
export const fetchInventarioResumen = createAsyncThunk(
  "bi/fetchInventarioResumen",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().app.user;
      const { filters, pagination } = getState().bi.inventarioResumen;
      const params = { ...filters, ...pagination };
      const qs = buildQueryString(params);
      return await apiFetch(`bi/inventario/resumen${qs}`, token, { dispatch });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- Ventas Diarias ---
export const fetchVentasDiarias = createAsyncThunk(
  "bi/fetchVentasDiarias",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().app.user;
      const { filters, pagination } = getState().bi.ventasDiarias;
      const params = { ...filters, ...pagination };
      const qs = buildQueryString(params);
      return await apiFetch(`bi/ventas-diarias${qs}`, token, { dispatch });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- Clientes Inactivos ---
export const fetchClientesInactivos = createAsyncThunk(
  "bi/fetchClientesInactivos",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().app.user;
      const { filters, pagination } = getState().bi.clientesInactivos;
      const params = { ...filters, ...pagination };
      const qs = buildQueryString(params);
      return await apiFetch(`bi/clientes-inactivos${qs}`, token, { dispatch });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- Apertura ---
export const fetchApertura = createAsyncThunk(
  "bi/fetchApertura",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().app.user;
      const { filters } = getState().bi.apertura;
      const qs = buildQueryString(filters);
      return await apiFetch(`bi/apertura${qs}`, token, { dispatch });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- Comparativo ---
export const fetchComparativo = createAsyncThunk(
  "bi/fetchComparativo",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().app.user;
      const { filters } = getState().bi.comparativo;
      const qs = buildQueryString(filters);
      return await apiFetch(`bi/comparativo${qs}`, token, { dispatch });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- Comparativo: Tendencia ---
export const fetchComparativoTendencia = createAsyncThunk(
  "bi/fetchComparativoTendencia",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().app.user;
      return await apiFetch("bi/comparativo/tendencia", token, { dispatch });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- Comparativo: Heatmap ---
export const fetchComparativoHeatmap = createAsyncThunk(
  "bi/fetchComparativoHeatmap",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().app.user;
      return await apiFetch("bi/comparativo/heatmap", token, { dispatch });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- Transferencias: Sugerencias ---
export const fetchTransferencias = createAsyncThunk(
  "bi/fetchTransferencias",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { token } = getState().app.user;
      const { filters, pagination } = getState().bi.transferencias;
      const params = { ...filters, ...pagination };
      const qs = buildQueryString(params);
      return await apiFetch(`bi/transferencias/sugerencias${qs}`, token, { dispatch });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================
// Initial State
// ============================================================
const initialState = {
  // Inventario
  inventario: {
    data: [],
    total: 0,
    loading: false,
    error: null,
    lastUpdated: null,
    filters: {
      busqueda: null,
      marca: null,
      proveedor: null,
      bodega: null,
      categoria: null,
      peso: null,
      estadoInventario: null,
      consolidado: false,
    },
    pagination: { limit: 50, skip: 0 },
  },
  // Inventario Simple (una fila por producto)
  inventarioResumen: {
    data: [],
    bodegas: [],
    total: 0,
    loading: false,
    error: null,
    lastUpdated: null,
    filters: {
      busqueda: null,
      marca: null,
      proveedor: null,
      categoria: null,
    },
    pagination: { limit: 50, skip: 0 },
  },
  // Ventas Diarias
  ventasDiarias: {
    indicadores: null,
    data: [],
    total: 0,
    loading: false,
    error: null,
    filters: {
      fecha: null, // default: hoy (se setea al montar el componente)
      tienda: null,
      estadoPago: null,
      metodoPago: null,
    },
    pagination: { limit: 50, skip: 0 },
  },
  // Clientes Inactivos
  clientesInactivos: {
    data: [],
    total: 0,
    loading: false,
    error: null,
    filters: {
      diasMin: 90,
      diasMax: 730,
      tienda: null,
      marca: null,
    },
    pagination: { limit: 50, skip: 0 },
  },
  // Apertura
  apertura: {
    data: [],
    top20Productos: [],
    totales: null,
    loading: false,
    error: null,
    filters: {
      agruparPor: "provincia",
      fechaInicio: null,
      fechaFin: null,
      distrito: null,
      marca: null,
      categoria: null,
    },
  },
  // Comparativo
  comparativo: {
    metricas: null,
    tablaComparativa: [],
    top20Crecimiento: [],
    top20Caida: [],
    tendencia: [],
    heatmap: [],
    loading: false,
    loadingTendencia: false,
    loadingHeatmap: false,
    error: null,
    filters: {
      vista: "mesAnterior",
      segmento: null,
      tienda: null,
      marca: null,
      categoria: null,
    },
  },
  // Transferencias
  transferencias: {
    sugerencias: [],
    total: 0,
    loading: false,
    error: null,
    filters: {
      estado: "pendiente",
      bodegaDestino: null,
    },
    pagination: { limit: 50, skip: 0 },
  },
};

// ============================================================
// Slice
// ============================================================
const biSlice = createSlice({
  name: "bi",
  initialState,
  reducers: {
    // --- Inventario filters & pagination ---
    setInventarioFilters: (state, action) => {
      state.inventario.filters = { ...state.inventario.filters, ...action.payload };
      state.inventario.pagination.skip = 0; // Reset pagination on filter change
    },
    setInventarioPagination: (state, action) => {
      state.inventario.pagination = { ...state.inventario.pagination, ...action.payload };
    },
    clearInventarioFilters: (state) => {
      state.inventario.filters = initialState.inventario.filters;
      state.inventario.pagination = initialState.inventario.pagination;
    },

    // --- Inventario Simple filters & pagination ---
    setInventarioResumenFilters: (state, action) => {
      state.inventarioResumen.filters = { ...state.inventarioResumen.filters, ...action.payload };
      state.inventarioResumen.pagination.skip = 0;
    },
    setInventarioResumenPagination: (state, action) => {
      state.inventarioResumen.pagination = { ...state.inventarioResumen.pagination, ...action.payload };
    },
    clearInventarioResumenFilters: (state) => {
      state.inventarioResumen.filters = initialState.inventarioResumen.filters;
      state.inventarioResumen.pagination = initialState.inventarioResumen.pagination;
    },

    // --- Ventas Diarias filters & pagination ---
    setVentasDiariasFilters: (state, action) => {
      state.ventasDiarias.filters = { ...state.ventasDiarias.filters, ...action.payload };
      state.ventasDiarias.pagination.skip = 0;
    },
    setVentasDiariasPagination: (state, action) => {
      state.ventasDiarias.pagination = { ...state.ventasDiarias.pagination, ...action.payload };
    },
    clearVentasDiariasFilters: (state) => {
      state.ventasDiarias.filters = initialState.ventasDiarias.filters;
      state.ventasDiarias.pagination = initialState.ventasDiarias.pagination;
    },

    // --- Clientes Inactivos filters & pagination ---
    setClientesInactivosFilters: (state, action) => {
      state.clientesInactivos.filters = { ...state.clientesInactivos.filters, ...action.payload };
      state.clientesInactivos.pagination.skip = 0;
    },
    setClientesInactivosPagination: (state, action) => {
      state.clientesInactivos.pagination = { ...state.clientesInactivos.pagination, ...action.payload };
    },
    clearClientesInactivosFilters: (state) => {
      state.clientesInactivos.filters = initialState.clientesInactivos.filters;
      state.clientesInactivos.pagination = initialState.clientesInactivos.pagination;
    },

    // --- Apertura filters ---
    setAperturaFilters: (state, action) => {
      state.apertura.filters = { ...state.apertura.filters, ...action.payload };
    },
    clearAperturaFilters: (state) => {
      state.apertura.filters = initialState.apertura.filters;
    },

    // --- Comparativo filters ---
    setComparativoFilters: (state, action) => {
      state.comparativo.filters = { ...state.comparativo.filters, ...action.payload };
    },
    clearComparativoFilters: (state) => {
      state.comparativo.filters = initialState.comparativo.filters;
    },

    // --- Transferencias filters & pagination ---
    setTransferenciasFilters: (state, action) => {
      state.transferencias.filters = { ...state.transferencias.filters, ...action.payload };
      state.transferencias.pagination.skip = 0;
    },
    setTransferenciasPagination: (state, action) => {
      state.transferencias.pagination = { ...state.transferencias.pagination, ...action.payload };
    },
    clearTransferenciasFilters: (state) => {
      state.transferencias.filters = initialState.transferencias.filters;
      state.transferencias.pagination = initialState.transferencias.pagination;
    },

    // --- Reset all BI state ---
    resetBIState: () => initialState,
  },
  extraReducers: (builder) => {
    // --- Inventario ---
    builder
      .addCase(fetchInventario.pending, (state) => {
        state.inventario.loading = true;
        state.inventario.error = null;
      })
      .addCase(fetchInventario.fulfilled, (state, action) => {
        state.inventario.loading = false;
        state.inventario.data = action.payload.data;
        state.inventario.total = action.payload.total;
        state.inventario.lastUpdated = action.payload.lastUpdated || new Date().toISOString();
      })
      .addCase(fetchInventario.rejected, (state, action) => {
        state.inventario.loading = false;
        state.inventario.error = action.payload || "Error al cargar inventario";
      });

    // --- Inventario Simple ---
    builder
      .addCase(fetchInventarioResumen.pending, (state) => {
        state.inventarioResumen.loading = true;
        state.inventarioResumen.error = null;
      })
      .addCase(fetchInventarioResumen.fulfilled, (state, action) => {
        state.inventarioResumen.loading = false;
        state.inventarioResumen.data = action.payload.data;
        state.inventarioResumen.bodegas = action.payload.bodegas || [];
        state.inventarioResumen.total = action.payload.total;
        state.inventarioResumen.lastUpdated = action.payload.lastUpdated || new Date().toISOString();
      })
      .addCase(fetchInventarioResumen.rejected, (state, action) => {
        state.inventarioResumen.loading = false;
        state.inventarioResumen.error = action.payload || "Error al cargar inventario simple";
      });

    // --- Ventas Diarias ---
    builder
      .addCase(fetchVentasDiarias.pending, (state) => {
        state.ventasDiarias.loading = true;
        state.ventasDiarias.error = null;
      })
      .addCase(fetchVentasDiarias.fulfilled, (state, action) => {
        state.ventasDiarias.loading = false;
        state.ventasDiarias.indicadores = action.payload.indicadores;
        state.ventasDiarias.data = action.payload.data;
        state.ventasDiarias.total = action.payload.total;
      })
      .addCase(fetchVentasDiarias.rejected, (state, action) => {
        state.ventasDiarias.loading = false;
        state.ventasDiarias.error = action.payload || "Error al cargar ventas diarias";
      });

    // --- Clientes Inactivos ---
    builder
      .addCase(fetchClientesInactivos.pending, (state) => {
        state.clientesInactivos.loading = true;
        state.clientesInactivos.error = null;
      })
      .addCase(fetchClientesInactivos.fulfilled, (state, action) => {
        state.clientesInactivos.loading = false;
        state.clientesInactivos.data = action.payload.data;
        state.clientesInactivos.total = action.payload.total;
      })
      .addCase(fetchClientesInactivos.rejected, (state, action) => {
        state.clientesInactivos.loading = false;
        state.clientesInactivos.error = action.payload || "Error al cargar clientes inactivos";
      });

    // --- Apertura ---
    builder
      .addCase(fetchApertura.pending, (state) => {
        state.apertura.loading = true;
        state.apertura.error = null;
      })
      .addCase(fetchApertura.fulfilled, (state, action) => {
        state.apertura.loading = false;
        state.apertura.data = action.payload.data;
        state.apertura.top20Productos = action.payload.top20Productos;
        state.apertura.totales = action.payload.totales;
      })
      .addCase(fetchApertura.rejected, (state, action) => {
        state.apertura.loading = false;
        state.apertura.error = action.payload || "Error al cargar reporte de apertura";
      });

    // --- Comparativo ---
    builder
      .addCase(fetchComparativo.pending, (state) => {
        state.comparativo.loading = true;
        state.comparativo.error = null;
      })
      .addCase(fetchComparativo.fulfilled, (state, action) => {
        state.comparativo.loading = false;
        state.comparativo.metricas = action.payload.metricas;
        state.comparativo.tablaComparativa = action.payload.tablaComparativa;
        state.comparativo.top20Crecimiento = action.payload.top20Crecimiento;
        state.comparativo.top20Caida = action.payload.top20Caida;
      })
      .addCase(fetchComparativo.rejected, (state, action) => {
        state.comparativo.loading = false;
        state.comparativo.error = action.payload || "Error al cargar comparativo";
      });

    // --- Comparativo: Tendencia ---
    builder
      .addCase(fetchComparativoTendencia.pending, (state) => {
        state.comparativo.loadingTendencia = true;
      })
      .addCase(fetchComparativoTendencia.fulfilled, (state, action) => {
        state.comparativo.loadingTendencia = false;
        state.comparativo.tendencia = action.payload;
      })
      .addCase(fetchComparativoTendencia.rejected, (state, action) => {
        state.comparativo.loadingTendencia = false;
        state.comparativo.error = action.payload || "Error al cargar tendencia";
      });

    // --- Comparativo: Heatmap ---
    builder
      .addCase(fetchComparativoHeatmap.pending, (state) => {
        state.comparativo.loadingHeatmap = true;
      })
      .addCase(fetchComparativoHeatmap.fulfilled, (state, action) => {
        state.comparativo.loadingHeatmap = false;
        state.comparativo.heatmap = action.payload;
      })
      .addCase(fetchComparativoHeatmap.rejected, (state, action) => {
        state.comparativo.loadingHeatmap = false;
        state.comparativo.error = action.payload || "Error al cargar heatmap";
      });

    // --- Transferencias ---
    builder
      .addCase(fetchTransferencias.pending, (state) => {
        state.transferencias.loading = true;
        state.transferencias.error = null;
      })
      .addCase(fetchTransferencias.fulfilled, (state, action) => {
        state.transferencias.loading = false;
        state.transferencias.sugerencias = action.payload.data;
        state.transferencias.total = action.payload.total;
      })
      .addCase(fetchTransferencias.rejected, (state, action) => {
        state.transferencias.loading = false;
        state.transferencias.error = action.payload || "Error al cargar transferencias";
      });
  },
});

export const {
  setInventarioFilters,
  setInventarioPagination,
  clearInventarioFilters,
  setInventarioResumenFilters,
  setInventarioResumenPagination,
  clearInventarioResumenFilters,
  setVentasDiariasFilters,
  setVentasDiariasPagination,
  clearVentasDiariasFilters,
  setClientesInactivosFilters,
  setClientesInactivosPagination,
  clearClientesInactivosFilters,
  setAperturaFilters,
  clearAperturaFilters,
  setComparativoFilters,
  clearComparativoFilters,
  setTransferenciasFilters,
  setTransferenciasPagination,
  clearTransferenciasFilters,
  resetBIState,
} = biSlice.actions;

export default biSlice.reducer;
